import type { ReactNode } from 'react';
import { toast } from 'sonner';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { DatePickerField } from '@/shared/ui/components';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FieldLabel } from '@/settings/shared/section-card';
import {
  UploadImageDialog,
} from '@/shared/ui/components/upload-image-dialog';
import { useUploadImageFile } from '@/services/common/use-upload-image';
import { useLicenseAuthorities } from '@/settings/services';
import type { PracticeSetupDetail } from '@/types/settings-api';
import {
  dateInputToWireIso,
  isoToDateInput,
} from '../practice-edit-merge';
import { PracticeErxSection } from '../practice-erx-section';

/**
 * Practice Details tab (PROD-758 + PROD-790).
 *
 * Ported from the deleted PracticeEditDialog Details tab (PR #41). Fields
 * mirror Flutter `practice_details_form_widget.dart`.
 *
 * PROD-790 additions:
 *   - License Authority picker (master-data dropdown via
 *     `useLicenseAuthorities` — wire-shape verified by dev cURL 2026-05-23,
 *     7 UAE authorities: DHA / DHCC / DOH / CDA / EHA / HAAD / MOH).
 *   - Practice Image upload (Azure blob via multipart `useUploadImageFile`, area `"PRACTICE"`).
 *   - Practice Stamp upload (same flow, separate slot).
 *
 * Reminder Profiles + Exam Rooms live on the Schedule tab (PROD-790, wired) —
 * not here. This tab is Practice Details only.
 *
 * Mandatory: practice name. Optional: licence number + expiration +
 * description + tax id + authority + image + stamp.
 */
export interface PracticeDetailsTabProps {
  draft: PracticeSetupDetail;
  onChange: (next: PracticeSetupDetail) => void;
  canEdit: boolean;
}

export function PracticeDetailsTab({
  draft,
  onChange,
  canEdit,
}: PracticeDetailsTabProps) {
  const setField = <K extends keyof PracticeSetupDetail>(
    key: K,
    value: PracticeSetupDetail[K],
  ) => {
    onChange({ ...draft, [key]: value });
  };

  const { data: authorities = [], isLoading: authoritiesLoading } =
    useLicenseAuthorities();
  const uploadImage = useUploadImageFile();

  // Shared upload handler — caller passes the target field so we keep the
  // logic in one place. On null, just clear the field. On a File, upload via
  // the multipart /api/Master/UploadImageFile (area "PRACTICE", lowercased by
  // the hook) and write the returned URL into the draft.
  //
  // Uses the multipart endpoint, NOT the base64 /UploadImage: the base64
  // variant 200s with an EMPTY body on dev (returns no URL — see
  // reference_uploadimage_empty_body_dev), so a freshly uploaded practice
  // image/stamp never got a URL → the field stayed empty and the Save button
  // never went dirty. The multipart endpoint returns the URL. A `File` is
  // already a `Blob`, so it passes straight through.
  const handleImageChange = async (
    file: File | null,
    field: 'practiceImage' | 'practiceStampUrl',
  ) => {
    if (file === null) {
      setField(field, '');
      return;
    }
    try {
      const url = await uploadImage.mutateAsync({
        blob: file,
        area: 'PRACTICE',
        filename: file.name,
      });
      if (url) {
        setField(field, url);
      } else {
        // Multipart returns the URL on success or throws on failure, so an
        // empty string is unexpected — surface it rather than silently leaving
        // the field empty (which would also leave Save disabled).
        toast.error(
          'Could not save the image — the service returned no URL. Please try again.',
        );
      }
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to upload image.',
      );
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-sm font-medium text-brand-depth">
        Practice details
      </h2>
      <PlainCard>
        <div className="flex gap-8 items-start">
          <UploadImageDialog
            label="Practice image"
            value={draft.practiceImage || null}
            onChange={(file) => handleImageChange(file, 'practiceImage')}
          />
          <UploadImageDialog
            label="Practice stamp"
            value={draft.practiceStampUrl || null}
            onChange={(file) => handleImageChange(file, 'practiceStampUrl')}
          />
        </div>

        <div>
          <FieldLabel>
            Practice name <span className="text-status-error">*</span>
          </FieldLabel>
          <Input
            value={draft.practiceName}
            maxLength={100}
            placeholder="Enter practice name"
            disabled={!canEdit}
            onChange={(e) => setField('practiceName', e.target.value)}
          />
          {canEdit && draft.practiceName.trim().length === 0 && (
            <p className="mt-1 text-sm text-status-error">
              Practice name is required.
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <FieldLabel>License number</FieldLabel>
            <Input
              value={draft.licenseNumber}
              maxLength={100}
              placeholder="e.g. GD1234"
              disabled={!canEdit}
              onChange={(e) => setField('licenseNumber', e.target.value)}
            />
          </div>
          <div>
            <FieldLabel>License authority</FieldLabel>
            <Select
              value={draft.licenseAuthority || undefined}
              disabled={!canEdit || authoritiesLoading}
              onValueChange={(next) =>
                setField('licenseAuthority', next ?? '')
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue
                  placeholder={
                    authoritiesLoading ? 'Loading…' : 'Select an authority'
                  }
                >
                  {authorities.find((a) => a.Id === draft.licenseAuthority)?.Name}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {authorities.map((a) => (
                  <SelectItem key={a.Id} value={a.Id}>
                    {a.Name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <FieldLabel>License expiration date</FieldLabel>
          <DatePickerField
            value={isoToDateInput(draft.licenseExpirationDate)}
            disabled={!canEdit}
            onChange={(v) =>
              setField(
                'licenseExpirationDate',
                v ? dateInputToWireIso(v) : '',
              )
            }
          />
        </div>

        <div>
          <FieldLabel>Description</FieldLabel>
          <Input
            value={draft.practiceDescription}
            maxLength={500}
            placeholder="Describe this practice"
            disabled={!canEdit}
            onChange={(e) => setField('practiceDescription', e.target.value)}
          />
        </div>

        <div>
          <FieldLabel>Billing tax ID</FieldLabel>
          <Input
            value={draft.billingTaxId}
            maxLength={50}
            placeholder="Tax ID"
            disabled={!canEdit}
            onChange={(e) => setField('billingTaxId', e.target.value)}
          />
        </div>

        <ToggleRow
          label="Primary practice"
          description="Mark this as the organisation's primary practice."
          checked={draft.primaryPractice}
          disabled={!canEdit}
          onChange={(next) => setField('primaryPractice', next)}
        />
        <ToggleRow
          label="Billing enabled"
          description="Enable invoicing and claims for this practice."
          checked={draft.billingStatus}
          disabled={!canEdit}
          onChange={(next) => setField('billingStatus', next)}
        />
      </PlainCard>

      {/*
       * Practice eRx — eClaim / ePrescription toggles + credentials editor.
       * Only renders on edit (the eRx endpoint is per-practice; on create
       * mode there's no practiceSetupId yet, so the section is hidden
       * until the user saves the practice and lands back on edit).
       */}
      {draft.practiceSetupId && (
        <PracticeErxSection
          practiceSetupId={draft.practiceSetupId}
          canEdit={canEdit}
        />
      )}
    </div>
  );
}

function PlainCard({ children }: { children: ReactNode }) {
  return (
    <section className="rounded-lg border border-black/5 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04)] px-6 py-6 flex flex-col gap-6">
      {children}
    </section>
  );
}

function ToggleRow({
  label,
  description,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <Checkbox
        checked={checked}
        disabled={disabled}
        onCheckedChange={(next) => onChange(next === true)}
        className="mt-1"
      />
      <span className="flex-1">
        <span className="block text-sm font-medium text-text-default">
          {label}
        </span>
        <span className="block text-sm text-text-muted">{description}</span>
      </span>
    </label>
  );
}
